// ---------------------------------------------------------------------------
// Which flagged features this account has (NDI-239 v2)
//
// GET /api/features answers `{ok, features: string[]}` for the signed-in
// account (server: v21/features.ts — user and organisation flags, combined).
// Asked once per page load: every caller shares one promise. A failed
// request counts as "no features" for that caller and is asked again next
// time, so a blip does not hide the feature until a reload.
// ---------------------------------------------------------------------------

let cached: Promise<ReadonlySet<string>> | null = null;

function load(): Promise<ReadonlySet<string>> {
  const request = fetch('/api/features')
    .then(resp => (resp.ok ? resp.json() : { ok: false }))
    .then((data: { ok?: boolean; features?: unknown }) => {
      if (!data?.ok || !Array.isArray(data.features)) throw new Error('features unavailable');
      return new Set(data.features.filter((f): f is string => typeof f === 'string'));
    });
  request.catch(() => {
    if (cached === request) cached = null;
  });
  return request;
}

/** True when the signed-in account has the flagged feature `name`. */
export function hasFeature(name: string): Promise<boolean> {
  cached ??= load();
  return cached.then(
    set => set.has(name),
    () => false,
  );
}

/** Forgets the answer — for tests, and after an admin changed the flags in this tab. */
export function resetFeatureCache(): void {
  cached = null;
}
